<template>
  <div
    class="v-switcher__wrapper"
    :class="{
      ['disabled']: disabled,
      [type]: true,
      [extraClass]: !!extraClass
    }"
  >
    <div
      v-if="type === 'toggle' || type === 'toggleReverse'"
      :class="{ ['reverse-toggle']: type === 'toggleReverse' }"
    >
      <span
        :class="{
          ['active']: type === 'toggleReverse' ? !_leftSelected : _leftSelected
        }"
      >
        {{ switcherKeys.left && switcherKeys.left.label }}</span
      >
      <div
        class="internal-switch-wrapper"
        :class="{ ['internal-active']: !_leftSelected }"
        @click="_onClick"
      >
        <div class="dot" :class="{ ['left']: _leftSelected }"></div>
      </div>
      <span
        :class="{
          ['active']: type === 'toggleReverse' ? _leftSelected : !_leftSelected
        }"
      >
        {{ switcherKeys.right && switcherKeys.right.label }}
      </span>
    </div>

    <div v-if="type === 'element'">
      <div
        v-for="item in switcherKeys"
        class="element-wrapper"
        :class="{
          ['active']: item.key === activeKey,
          ['no-label']: !item.label
        }"
        @click="e => _onClick(e, item.key)"
      >
        <img
          :src="item.key === activeKey ? item.iconActive : item.icon"
          alt="switcher"
        />
        <span v-if="item.label">{{ item.label }}</span>
      </div>
    </div>

    <div
      v-if="type === 'single'"
      class="single-wrapper"
      :class="{
        ['no-label']: !switcherKeys.label,
        ['hovered']: !!switcherHovered
      }"
      @mouseover="switcherHovered = true"
      @mouseleave="switcherHovered = false"
      @click="e => _onClick(e, switcherKeys.key)"
    >
      <span v-if="switcherKeys.label">{{ switcherKeys.label }}</span>
      <img
        :src="switcherHovered ? switcherKeys.iconActive : switcherKeys.icon"
        alt="switcher"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import VDynamicIcon from "@/components/VDynamicIcon";

export default Vue.component("VSwitcher", {
  props: {
    activeKey: VueTypes.oneOfType([VueTypes.string, VueTypes.bool]),
    switcherKeys: VueTypes.any,
    disabled: VueTypes.bool.def(false),
    extraClass: VueTypes.string,
    type: VueTypes.oneOf(["toggle", "element", "toggleReverse", "single"]).def(
      "toggle"
    ),
    onClickHandler: VueTypes.func
  },

  components: {
    VDynamicIcon
  },

  data() {
    return {
      switcherHovered: false
    };
  },

  methods: {
    _onClick(e, key) {
      if (key && key === this.activeKey) {
        return;
      }

      let active = key;

      if (this.type === "toggle" || this.type === "toggleReverse") {
        const { left, right } = this.switcherKeys;
        active = this._leftSelected ? right.key : left.key;
      }

      this.$emit("change", active);
      this.onClickHandler && this.onClickHandler(active);
    }
  },

  computed: {
    _leftSelected() {
      if (!this.switcherKeys) {
        return;
      }

      const { left } = this.switcherKeys;

      if (!left) {
        return;
      }

      return left.key === this.activeKey;
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-switcher__wrapper";

/*---For toggle switcher---*/
.#{$ns} {
  &.toggle,
  &.toggleReverse {
    div {
      display: flex;
      align-items: center;
      height: 16px;

      .internal-switch-wrapper {
        position: relative;
        width: 30px;
        height: 16px;
        cursor: pointer;

        border-radius: 50px;
        border: 1px solid $primary-color;
        margin: 0 15px;

        .dot {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: $primary-color;
          position: absolute;
          top: -1px;
          right: -1px;
          transition: left 0.15s linear, right 0.15s linear;

          &.left {
            left: -1px;
            right: auto;
          }
        }
      }

      span {
        font-size: 18px;
        font-weight: 500;
        color: $primary-grey;

        &.active {
          color: $primary-color;
        }
      }
    }
  }

  &.toggleReverse {
    div {
      display: flex;
      align-items: center;
      height: 30px;

      span {
        font-size: 15px;
        font-family: Lato-Light, sans-serif;
        color: $primary-grey;

        &.active {
          color: $primary-color;
        }
      }

      .internal-switch-wrapper {
        border-color: transparent;
        background: rgba(196, 196, 196, 0.65);
        height: 5px;
        margin: 0 0 0 10px;

        .dot {
          background-color: $primary-grey;
        }

        &.internal-active {
          background: rgba(87, 87, 245, 0.6);

          .dot {
            background-color: $primary-color;
          }
        }

        .dot {
          top: -7px;
          right: -1px;
        }
      }
    }
  }
}

/*---For on element switcher---*/
.#{$ns}.element {
  div {
    display: flex;
    align-items: center;
    height: 30px;
    justify-content: center;
    vertical-align: center;

    .internal-switch-wrapper {
      position: relative;
      width: 30px;
      height: 16px;
      cursor: pointer;

      border-radius: 50px;
      border: 1px solid $primary-color;
      margin: 0 15px;

      .dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: $primary-color;
        position: absolute;
        top: -1px;
        right: -1px;
        transition: left 0.15s linear, right 0.15s linear;

        &.left {
          left: -1px;
          right: auto;
        }
      }
    }

    div {
      font-weight: 500;
      display: inline-block;
      color: $primary-grey;
      border: 1px solid #c4c4c4;
      font-size: 18px;
      line-height: 1.4;
      width: fit-content;
      padding: 0 3px;
      height: 30px;
      cursor: pointer;

      &.active {
        color: $primary-color;
        border-color: $primary-color;
        cursor: default;
      }
    }

    :first-child {
      border-radius: 2px 0 0 2px;
    }

    :last-child {
      border-radius: 0 2px 2px 0;
    }
  }

  .element-wrapper {
    display: flex;
    align-items: center;
    min-width: 125px;

    span {
      margin-left: 5px;
    }

    &.no-label {
      min-width: 35px;
    }
  }

  &.disabled {
    div {
      /*
      border: 1px solid $primary-grey;
*/
      .dot {
        background-color: $primary-grey;
      }
    }
    .active {
      color: $primary-grey;
    }
  }
}

.single-wrapper {
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  font-size: 14px;
  color: $secondary-text-color;
  cursor: pointer;

  &:hover {
    color: $primary-color;
  }

  img {
    margin-left: 10px;
  }
}
</style>
